import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { MemoryRouter } from 'react-router-dom';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import FloatingDocumentPiPContent from '@src/components/FloatingDocumentPIPWindow/FloatingDocumentPiPContent';
import { INFO_MODAL_ICONS } from '@src/constants/infoModal.constants';
import { ThemeProvider, useRoomContext } from '@src/contexts';
import { useNotificationStore, useRoomActionsStore, useRoomStore } from '@src/stores';
import { openURL } from '@src/utils/browser.utils';

export function useDocumentPiP() {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [error, setError] = useState<string>('');

  const { isWebinarAttendee, isCallEnded } = useRoomContext();
  const setPipActive = useRoomActionsStore((state) => state.pipSettings.onClick);

  const showPIP = useCallback(async () => {
    try {
      if (pipWindow) return;

      const win = await (window as any).documentPictureInPicture.requestWindow({
        width: 348,
        height: 489,
      });

      // Inject CSS links from main document
      const head = win.document.head;
      const links = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'));

      links.forEach((link) => {
        const clonedLink = link.cloneNode(true);
        if (link.tagName === 'STYLE') {
          const styleSheet = (link as HTMLStyleElement).sheet;
          if (styleSheet && (!link.textContent || link.textContent.trim() === '')) {
            try {
              const cssRules = Array.from(styleSheet.cssRules)
                .map((rule) => rule.cssText)
                .join('');
              clonedLink.textContent = cssRules;
            } catch (e) {
              // Ignore CORS errors for external stylesheets
            }
          }
        }
        head.appendChild(clonedLink);
      });

      const pipRoot = win.document.documentElement;

      const appTheme = document.documentElement.getAttribute('data-theme')!;
      pipRoot.setAttribute('data-theme', appTheme);

      const applyAppThemeVariablesToPip = () => {
        const rootStyles = getComputedStyle(document.documentElement);
        for (let i = 0; i < rootStyles.length; i++) {
          const propName = rootStyles[i];
          if (propName.startsWith('--')) {
            pipRoot.style.setProperty(propName, rootStyles.getPropertyValue(propName));
          }
        }
      };

      applyAppThemeVariablesToPip();

      win.document.body.style.backgroundColor = 'var(--background-bg)';
      pipRoot.style.backgroundColor = 'var(--background-bg)';

      // Create a clean mount point
      const container = win.document.createElement('div');
      container.id = 'pip-root';
      Object.assign(container.style, {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      });

      win.document.body.innerHTML = '';
      win.document.body.appendChild(container);

      // Create a separate Emotion cache for the pip window
      const pipCache = createCache({
        key: 'mui-pip',
        container: head,
      });

      ReactDOM.createRoot(container).render(
        <MemoryRouter>
          <ThemeProvider ignorePathCheck targetDocument={win.document}>
            <CacheProvider value={pipCache}>
              <FloatingDocumentPiPContent pipWindow={win} />
            </CacheProvider>
          </ThemeProvider>
        </MemoryRouter>
      );

      win.addEventListener('pagehide', () => {
        setPipWindow(null);
        setPipActive(false);

        if (useNotificationStore.getState().persistentNotification) {
          useNotificationStore.getState().clearPersistentNotification();

          useRoomStore.getState().setInfoModalContent({
            icon: INFO_MODAL_ICONS.CONNECTION_ISSUE,
            title: 'There was a connection issue',
            footer: {
              isLoading: true,
              text: 'Reconnecting',
            },
            handleOnCloseModal: () => {
              openURL('/rejoin');
            },
          });
          useRoomStore.getState().setIsInfoModalOpen(true);
        }
      });

      setPipWindow(win);
      setPipActive(true);

      setTimeout(() => {
        if (useRoomStore.getState().isInfoModalOpen) {
          useRoomStore.getState().setIsInfoModalOpen(false);
        }
      }, 100);
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    }
  }, [pipWindow, setPipActive]);

  const hidePip = useCallback(() => {
    if (pipWindow && !pipWindow.closed) {
      pipWindow.close();
      setPipWindow(null);
      setPipActive(false);

      if (useNotificationStore.getState().persistentNotification) {
        useNotificationStore.getState().clearPersistentNotification();

        useRoomStore.getState().setInfoModalContent({
          icon: INFO_MODAL_ICONS.CONNECTION_ISSUE,
          title: 'There was a connection issue',
          footer: {
            isLoading: true,
            text: 'Reconnecting',
          },
          handleOnCloseModal: () => {
            openURL('/rejoin');
          },
        });
        useRoomStore.getState().setIsInfoModalOpen(true);
      }
    }
  }, [pipWindow, setPipActive]);

  const togglePip = useCallback(async () => {
    if (!pipWindow) {
      await showPIP();
    } else {
      hidePip();
      setPipWindow(null);
    }
  }, [pipWindow, showPIP, hidePip]);

  useEffect(() => {
    const enableFakeMediaStream = async () => {
      if (isWebinarAttendee) return;

      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        (navigator.mediaSession as any).setActionHandler('enterpictureinpicture', showPIP);
      } catch (err: any) {
        console.error(err);
        setError(err.message);
      }
    };

    enableFakeMediaStream();

    return () => {
      try {
        (navigator.mediaSession as any).setActionHandler('enterpictureinpicture', null);
      } catch {}
    };
  }, [isWebinarAttendee, showPIP]);

  // Close PIP window when the meeting ends
  useEffect(() => {
    if (isCallEnded && pipWindow && !pipWindow.closed) {
      hidePip();
    }
  }, [isCallEnded, pipWindow, hidePip]);

  return { togglePip, showPIP, hidePip, error };
}
